import { EmptyState } from '@/components/layout/empty-state';
import { StateBadge } from '@/components/opportunity/state-badge';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { edgeColorClass, formatEdge } from '@/lib/format-edge';
import { formatOdds } from '@/lib/format-odds';
import { formatRelativeTime } from '@/lib/format-relative-time';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { History } from 'lucide-react';
import { Link } from 'react-router-dom';

// Historial — terminal-state opportunities (executed / passed / expired /
// closed). Operator scans here for "what did I do today" / "what got away".
// The V1 view is a flat list ordered by operator action / expiry time.
// V2 will add filters + replay engine (architecture § 21.4.3).

const TERMINAL_STATES = ['executed', 'passed', 'closed', 'expired'] as const;

export function AlertsHistoryPage() {
  const opps = trpc.opportunities.list.useQuery({
    state: [...TERMINAL_STATES],
    sortBy: 'firstDetectedAt',
    limit: 200,
  });

  if (opps.isLoading) {
    return (
      <div className="space-y-4">
        <header>
          <h1
            className="font-brand text-3xl tracking-[0.25em] text-foreground"
            data-testid="page-title"
          >
            HISTORIAL
          </h1>
          <p className="text-xs text-muted-foreground" data-testid="page-description">
            Cargando…
          </p>
        </header>
        <Skeleton className="h-64" />
      </div>
    );
  }

  const rows = opps.data ?? [];

  if (rows.length === 0) {
    return (
      <EmptyState
        title="HISTORIAL"
        description="Acá aparecen todas las alertas que ya cerraste — ejecutadas, descartadas, expiradas o cerradas por el scanner."
        value="0"
        unit="alertas en el historial"
        hint="Una alerta entra al historial cuando hace click en Ejecutar / Descartar, expira por tiempo, o el scanner la cierra. Volvé cuando hayas accionado tu primera."
        icon={History}
      />
    );
  }

  return (
    <div className="space-y-4">
      <header className="flex items-baseline justify-between">
        <h1
          className="font-brand text-3xl tracking-[0.25em] text-foreground"
          data-testid="page-title"
        >
          HISTORIAL
        </h1>
        <span className="font-mono text-xs text-muted-foreground" data-testid="page-description">
          {rows.length} {rows.length === 1 ? 'alerta' : 'alertas'} en historial
        </span>
      </header>
      <Card className="divide-y divide-border">
        {rows.map((o) => (
          <Link
            key={o.id}
            to={`/matches/${o.canonicalMatchId}`}
            className="flex items-center justify-between gap-4 px-4 py-3 transition-colors hover:bg-popover"
          >
            <div className="flex items-center gap-3">
              <StateBadge state={o.state} />
              <div className="min-w-0">
                <p className="text-sm text-foreground">
                  {o.market.toUpperCase()} · {o.selection.toUpperCase()} · target{' '}
                  {formatOdds(o.targetOdds)}
                </p>
                <p className="font-mono text-[10px] text-muted-foreground">
                  detectada {formatRelativeTime(o.firstDetectedAt)} · match{' '}
                  {o.canonicalMatchId.slice(0, 8)}
                </p>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1 text-right">
              <span
                className={cn('font-mono text-base font-medium', edgeColorClass(o.currentEdgePct))}
              >
                {formatEdge(o.currentEdgePct)}
              </span>
              <span className="font-mono text-xs text-muted-foreground">
                {formatOdds(o.currentBestOdds)} {o.currentBestBook ? `· ${o.currentBestBook}` : ''}
              </span>
            </div>
          </Link>
        ))}
      </Card>
    </div>
  );
}
